import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The two emailed-link routes, built here so `router.tsx` gains exactly one line each (plan-014 GAP; the
 * operator-action confirmation's precedent, `../confirm-operator-action/confirm-operator-action-route.tsx`).
 *
 * Children of the ROOT, never of the shell: the link may be opened in a browser where nobody is signed in, and the
 * shell's guard would send the person to log in first. The token is the link's FRAGMENT (MAIL's `tokenLinkOf`), which
 * never reaches a server log, a proxy or a Referer header. It is read once and then taken out of the address bar, so
 * it lingers in no history entry or screenshot; nothing is posted until the person presses the button.
 */
import { createRoute, useLocation, useNavigate, type AnyRoute } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";

import { EmailChangeScreen, VerifyEmailScreen } from "./mail-landing-screen.js";

/** MAIL's paths (`apps/api/src/mail/link.ts` `MAIL_LINK_PATH`); the console restates them, as apps never import apps. */
export const VERIFY_EMAIL_PATH = "/verify-email";
export const EMAIL_CHANGE_PATH = "/me/security/email-change";

/** The fragment's token, kept in state; the address bar is replaced by the bare path. */
function useFragmentToken(path: typeof VERIFY_EMAIL_PATH | typeof EMAIL_CHANGE_PATH): string {
  const hash = useLocation({ select: (location) => location.hash.replace(/^#/, "") });
  const [token] = useState(hash);
  const navigate = useNavigate();
  useEffect(() => {
    if (hash !== "") void navigate({ to: path, hash: "", replace: true });
  }, [hash, navigate, path]);
  return token;
}

function VerifyEmailRoute(): ReactNode {
  usePortalTranslation("core");
  return <VerifyEmailScreen token={useFragmentToken(VERIFY_EMAIL_PATH)} />;
}

function EmailChangeRoute(): ReactNode {
  usePortalTranslation("core");
  return <EmailChangeScreen token={useFragmentToken(EMAIL_CHANGE_PATH)} />;
}

export function verifyEmailRouteUnder<TParent extends AnyRoute>(parent: TParent) {
  return createRoute({
    getParentRoute: () => parent,
    path: VERIFY_EMAIL_PATH,
    staticData: { pageTitle: portalText("core", "mail_landing.confirm_your_email_address_724104") },
    component: VerifyEmailRoute,
  });
}

export function emailChangeRouteUnder<TParent extends AnyRoute>(parent: TParent) {
  return createRoute({
    getParentRoute: () => parent,
    path: EMAIL_CHANGE_PATH,
    staticData: { pageTitle: portalText("core", "mail_landing.confirm_your_new_email_address_f5ab53") },
    component: EmailChangeRoute,
  });
}
